import React, { useState } from "react";
import Link from "next/link";
import { randomString } from "common";
import { ArticleGrid } from "components/atoms/articleGrid";
import { ArticleCard } from "components/molecules/articleCard/ArticleCard";
import { CompanyCard } from "components/molecules/companyCard";
import { CompanyGridNew } from "components/atoms/companyGridNew";
import { InView } from "react-intersection-observer";
import Loader from "react-loaders";
import { PageLayout } from "components/molecules/pageLayout";
import { OneColumn } from "components/atoms/oneColumn";
import { EventGrid } from "components/atoms/eventGrid";
import { EventCard } from "components/molecules/eventCard";
import { useCurrentEntor, useGQLClient } from "contexts/appContext";
import { Headline } from "components/atoms/headline";
import { Button } from "components/atoms/button";
import { InterviewBanner } from "components/atoms/interviewBanner";
import config from "config";

import { Routes } from "../../generated/routes";
import { MyPageHero } from "./MyPageHero";
import Hero from "./Hero";
import styles from "./PageHome.module.scss";
import {
  ClientItem,
  GroupedEventInstanceItem,
  FeaturedArticleItem,
} from "../../types";
import { wrap } from "../../lib/hoc";
import {
  ClientStatus,
  FeaturedArticleSort,
  FeaturedArticleStatus,
} from "../../generated/graphql";

type HomePageProps = {
  groupedEventInstances: GroupedEventInstanceItem[];
  canApplyForInt?: boolean;
};

const PageHome: React.FC<HomePageProps> = ({
  groupedEventInstances,
  canApplyForInt = false,
}) => {
  const currentEntor = useCurrentEntor();
  const { gqlClient } = useGQLClient();
  const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [articlesLoaded, setArticlesLoaded] = useState<boolean>(false);
  const [articles, setArticles] = useState<FeaturedArticleItem[]>([]);

  return (
    <PageLayout showLoginRegister>
      {currentEntor ? (
        <MyPageHero
          name={`${currentEntor.user.lastName} ${currentEntor.user.firstName}`}
        />
      ) : (
        <Hero />
      )}
      <OneColumn className={styles.oneColumnPadding}>
        {canApplyForInt && <InterviewBanner />}
        <Headline
          className={styles.headline}
          main="LIST OF EVENTS"
          sub="イベント一覧"
        />
        <EventGrid>
          {groupedEventInstances.map((groupedEventInstance) => (
            <Link
              key={`${groupedEventInstance.instanceType}${
                groupedEventInstance.instanceType ===
                "GroupedScheduledEventDates"
                  ? groupedEventInstance.scheduledEvent
                  : groupedEventInstance.id
              }`}
              href={Routes.url(
                "/events/[hash]",
                groupedEventInstance.instanceType ===
                  "GroupedScheduledEventDates"
                  ? {
                      hash: groupedEventInstance.scheduledEvent.event.hash,
                      venue:
                        groupedEventInstance.selectedScheduledEventDates[0].id,
                    }
                  : {
                      hash: groupedEventInstance.event.hash,
                    },
              )}
            >
              <a>
                <EventCard eventInstance={groupedEventInstance} />
              </a>
            </Link>
          ))}
        </EventGrid>
        <div className={styles.buttonContainer}>
          <Link href={Routes.url("/events/page/[page]", { page: 1 })}>
            <a>
              <Button color="secondary" fluid>
                一覧を見る
              </Button>
            </a>
          </Link>
        </div>
        <InView
          as="div"
          onChange={async (inView) => {
            if (inView && !clientsLoaded) {
              const {
                clients: {
                  edges: { node },
                },
              } = await gqlClient.ListClients({
                filter: {
                  first: 12,
                  salt: randomString(3),
                  status: ClientStatus.Visible,
                },
              });
              setClients(node);
              setClientsLoaded(true);
            }
          }}
        >
          <Headline
            className={styles.headline}
            main="RECOMMENDED COMPANIES"
            sub="おすすめ企業"
          />
          {clients.length === 0 || !clientsLoaded ? (
            <Loader active type="ball-scale-multiple" />
          ) : (
            <CompanyGridNew>
              {clients.map((client) => (
                <CompanyCard client={client} small key={client.id} />
              ))}
            </CompanyGridNew>
          )}
          <div className={styles.buttonContainerMargin}>
            <Link href={Routes.url("/companies/page/[page]", { page: 1 })}>
              <a>
                <Button color="secondary" fluid>
                  企業一覧を見る
                </Button>
              </a>
            </Link>
          </div>
        </InView>
        <InView
          as="div"
          onChange={async (inView) => {
            if (inView && !articlesLoaded) {
              const { featuredArticles } = await gqlClient.ListFeaturedArticles(
                {
                  filter: {
                    status: FeaturedArticleStatus.Active,
                    limit: 6,
                    sort: FeaturedArticleSort.DisplayOrderAsc,
                  },
                },
              );
              setArticles(featuredArticles);
              setArticlesLoaded(true);
            }
          }}
        >
          <Headline
            className={styles.headline}
            main="SPECIAL ARTICLES"
            sub="特集記事"
          />
          {articles.length === 0 || !articlesLoaded ? (
            <Loader active type="ball-scale-multiple" />
          ) : (
            <ArticleGrid>
              {articles.map((a) => (
                <ArticleCard article={a.article} key={a.id} noColorTitle />
              ))}
            </ArticleGrid>
          )}
          <div className={styles.buttonContainer}>
            <Link href={Routes.url("/articles/page/[page]", { page: 1 })}>
              <a>
                <Button color="secondary" fluid>
                  記事一覧を見る
                </Button>
              </a>
            </Link>
          </div>
        </InView>
      </OneColumn>
    </PageLayout>
  );
};

export default wrap(PageHome, async () => {
  return {
    status: "redirectTo",
    permanent: true,
    path: `${config.BAKLAVA_URL}`,
  };
});
